"use client";

import Link from "next/link";
import React, { useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

export default function RefundPolicyPage() {
  const [expandedSections, setExpandedSections] = useState<number[]>([0]);

  const toggleSection = (sectionIndex: number) => {
    setExpandedSections((prev) =>
      prev.includes(sectionIndex)
        ? prev.filter((index) => index !== sectionIndex)
        : [...prev, sectionIndex],
    );
  };

  const sections = [
    {
      title: "Overview",
      content: (
        <>
          <p className="text-text-light">
            At Sell It Off we prioritize transparency, buyer safety, and seller
            fairness. Payments are processed on the platform; funds are held
            until pickup is handled according to our process. This page is the
            full refund policy. Our{" "}
            <Link href="/terms" className="text-blue-600 hover:underline">
              Terms of Service
            </Link>{" "}
            (including Section 6) incorporates these rules by reference.
          </p>
        </>
      ),
    },
    {
      title: "When you are eligible for a full refund (100%)",
      content: (
        <>
          <p className="text-text-light">
            A <strong>full refund</strong> applies only at the pickup location,
            <strong> before you confirm receipt</strong>, in any of these cases:
          </p>
          <ul className="mt-4 list-disc pl-6 text-text-light">
            <li>
              There is a clear mismatch between the advertised description
              (including photos) and the actual condition or specifications of
              the item.
            </li>
            <li>
              The item has a significant defect that was not disclosed in the
              listing.
            </li>
            <li>
              The item is no longer available, or the seller withdraws, and the
              reason is not due to your delay or lack of communication after
              payment.
            </li>
          </ul>
          <p className="mt-4 text-text-light">
            If any of the above applies, do <strong>not</strong> confirm pickup.
            Contact support immediately so we can process a full refund.
          </p>
        </>
      ),
    },
    {
      title: "When no refund is issued",
      content: (
        <>
          <ul className="list-disc pl-6 text-text-light">
            <li>
              After you have picked up the item, confirmed receipt, and the
              seller has been paid.
            </li>
            <li>
              For issues you could reasonably have noticed at pickup but did not
              raise before confirming.
            </li>
            <li>
              For buyer&apos;s remorse or problems reported only after pickup is
              confirmed.
            </li>
          </ul>
          <p className="mt-4 text-text-light">
            Sell It Off is not liable for defects or disputes that arise after
            pickup is confirmed. Always inspect and, where relevant, test the
            item before you confirm.
          </p>
        </>
      ),
    },
    {
      title: "48-hour pickup window, item unlock, and 5% fee",
      content: (
        <>
          <p className="text-text-light">
            After you pay, you receive the seller&apos;s details and must
            schedule inspection and pickup within <strong>48 hours</strong> of
            receiving those details, and stay in prompt contact with the seller.
          </p>
          <p className="mt-4 text-text-light">
            If you do not meet that obligation and the sale cannot complete (for
            example the item becomes unavailable or the seller needs to offer it
            to someone else), the reserved transaction may be released so the
            item can be unlocked for other buyers.
          </p>
          <p className="mt-4 text-text-light">
            In that situation, if a refund is due to you, we will deduct a{" "}
            <strong>5% administrative fee</strong> from your payment; the
            remainder is refunded to your original payment method (subject to
            processing times from banks or card issuers).
          </p>
          <p className="mt-4 text-text-light">
            This fee covers payment handling, support, and the cost of releasing
            the hold so the marketplace can stay fair for sellers who held the
            item for you.
          </p>
        </>
      ),
    },
    {
      title: "How to request a refund",
      content: (
        <>
          <ol className="list-decimal pl-6 text-text-light">
            <li className="mt-1">
              At pickup, if the item does not match the listing or has an
              undisclosed serious defect, do not confirm receipt.
            </li>
            <li className="mt-1">
              Contact us right away via{" "}
              <Link
                href="/contact-us"
                className="text-blue-600 hover:underline"
              >
                Contact Us
              </Link>
              , WhatsApp, or{" "}
              <a
                href="mailto:support@sellitoffnow.com"
                className="text-blue-600 hover:underline"
              >
                support@sellitoffnow.com
              </a>
              , with a clear description of the issue and photos if possible.
            </li>
            <li className="mt-1">
              We will review the case and, where policy allows, process your
              refund.
            </li>
          </ol>
          <p className="mt-4 text-text-light">
            In-app refund requests may be added later; until then, support
            channels above are the official path.
          </p>
        </>
      ),
    },
    {
      title: "Disputes",
      content: (
        <p className="text-text-light">
          If buyers and sellers disagree, Sell It Off may review listing
          content, messages, and evidence submitted. We aim for a fair outcome
          and reserve the right to make a final decision. See{" "}
          <Link href="/terms" className="text-blue-600 hover:underline">
            Terms of Service — Dispute Resolution
          </Link>{" "}
          for related terms.
        </p>
      ),
    },
    {
      title: "Changes to this policy",
      content: (
        <p className="text-text-light">
          We may update this policy from time to time. Material changes will be
          communicated where practical (for example by email or in-app). The
          date below reflects the latest revision shown on this page.
        </p>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="mb-4 text-4xl font-bold text-foreground">Refund Policy</h1>
      <p className="mb-8 text-text-light">
        How refunds work on Sell It Off — eligibility, timing, and fees.
      </p>

      <div className="prose prose-lg max-w-none">
        <p className="mb-8 text-text-light">
          Last updated: {new Date().toLocaleDateString()}
        </p>

        <div className="space-y-4 not-prose">
          {sections.map((section, index) => (
            <div key={index} className="overflow-hidden rounded-lg border">
              <button
                type="button"
                onClick={() => toggleSection(index)}
                className="flex w-full items-center justify-between bg-secondary p-4 text-left transition-colors hover:bg-tertiary"
              >
                <h2 className="text-xl font-semibold text-foreground">
                  {section.title}
                </h2>
                {expandedSections.includes(index) ? (
                  <ChevronUpIcon className="h-6 w-6 shrink-0 text-text-light" />
                ) : (
                  <ChevronDownIcon className="h-6 w-6 shrink-0 text-text-light" />
                )}
              </button>

              {expandedSections.includes(index) && (
                <div className="p-4">{section.content}</div>
              )}
            </div>
          ))}
        </div>

        <p className="mt-10 text-sm text-text-light">
          By completing a payment on Sell It Off, you agree to our{" "}
          <Link href="/terms" className="text-blue-600 hover:underline">
            Terms and Conditions
          </Link>{" "}
          and this Refund Policy.
        </p>
      </div>
    </div>
  );
}
